import * as cessnalib from "cessnalib";
import { Exception, Particle, vacuole } from "@euglena/compact";

const getParticles = (): Particle[] => {
    return (JSON.parse(localStorage.getItem("particles") || "[]")) as Particle[];
}

const setParticles = (particles: Particle[]) => {
    localStorage.setItem("particles", JSON.stringify(particles));
}

export class VacuoleLocalstorage extends vacuole.Vacuole {
    constructor(nucleus: vacuole.Nucleus) {
        super(nucleus);
    }

    async list<T extends Particle>(query: Partial<T>, options?: vacuole.ListOptions): Promise<Exception | T[]> {
        const particles = getParticles();
        let retVal: T[] = [];
        
        // Filter particles based on query
        for (const particle of particles) {
            if (cessnalib.js.Class.doesMongoCover(particle, query)) {
                retVal.push(particle as T);
            }
        }

        // Apply ordering if specified
        if (options?.orderBy) {
            retVal.sort((a, b) => {
                const aValue = a[options.orderBy as keyof T];
                const bValue = b[options.orderBy as keyof T];
                
                if (aValue === bValue) return 0;
                if (aValue === undefined || aValue === null) return 1;
                if (bValue === undefined || bValue === null) return -1;
                
                const comparison = aValue < bValue ? -1 : 1;
                return options.order === 'desc' ? -comparison : comparison;
            });
        }

        // Apply offset
        if (options?.offset) {
            retVal = retVal.slice(options.offset);
        }

        // Apply limit
        if (options?.limit) {
            retVal = retVal.slice(0, options.limit);
        }

        return retVal;
    }

    getAlive(): Promise<void | Exception> {
        return Promise.resolve();
    }

    async read<T extends Particle>(query: Partial<T>, count: number | "All" = 1): Promise<Exception | T[]> {
        const retVal: T[] = [];
        const particles = getParticles();
        for (let i = 0, len = 0; i < particles.length && (count === "All" || len < count); i++) {
            if (cessnalib.js.Class.doesMongoCover(particles[i], query)) {
                retVal.push(particles[i] as T);
                len++;
            }
        }
        return retVal;
    }

    async save<T extends Particle>(particle: T, query?: vacuole.Query<T>, count: number | "All" = 1): Promise<void | Exception> {
        let particles = getParticles();
        if (query) {
            let overrideCount = 0;
            for (let i = 0; i < particles.length && (count === "All" || overrideCount < count); i++) {
                if (cessnalib.js.Class.doesMongoCover(particles[i], query)) {
                    particles[i] = particle;
                    overrideCount++;
                }
            }
            if (overrideCount === 0) {
                particles.push(particle);
            }
        } else {
            particles = [...particles, particle];
        }
        setParticles(particles);
    }

    async saveAll<T extends Particle>(particles: T[]): Promise<void | Exception> {
        let existingParticles = getParticles();
        existingParticles = [...existingParticles, ...particles];
        setParticles(existingParticles);
    }

    async remove<T extends Particle>(query: Partial<T>, count: number | "All" = 1): Promise<void | Exception> {
        if (query) {
            let particles = getParticles();
            let removeCount = 0;
            for (let i = 0; i < particles.length && (count === "All" || removeCount < count); i++) {
                if (cessnalib.js.Class.doesMongoCover(particles[i], query)) {
                    particles.splice(i, 1);
                    removeCount++;
                }
            }
            setParticles(particles);
        }
    }
}
